import {
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { RHFTextField } from 'src/components/hook-form';
import RHFMarkdownEditor from 'src/components/markdown-editor/markdownEditor';
import RHFImageUpload from 'src/components/hook-form/rhf-image-upload';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import { addPackage } from 'src/services/package.service';
import { getClinicDoctors } from 'src/services/clinic.service';
import { searchProcedures } from 'src/services/procedures.service';

export default function ClinicAddPackageView() {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [procedureOptions, setProcedureOptions] = useState([]);
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [loadingProcedures, setLoadingProcedures] = useState(false);

  const [selectedConcerns, setSelectedConcerns] = useState([]);

  useEffect(() => {
    getClinicDoctors(user.clinic_id).then((doctors) =>
      setAvailableDoctors(doctors || [])
    );
  }, [user.clinic_id]);

  const packageSchema = Yup.object().shape({
    package_name: Yup.string().required(),
    price: Yup.number().required().positive().min(0),
    package_promo_image: Yup.array().of(Yup.string().nullable()),
    package_description: Yup.string(),
    pre_post_images: Yup.array().of(Yup.string()).nullable(),
    package_inclusions: Yup.array().of(Yup.string()).nullable(),
    package_exclusions: Yup.array().of(Yup.string()).nullable(),
    package_doctors: Yup.array().of(Yup.string()).nullable(),
    tags: Yup.array().of(Yup.string()).nullable(),
    concerns: Yup.array().of(Yup.string()).nullable(),
  });

  const defaultValues = {
    package_name: '',
    price: 0,
    package_doctors: [],
    package_promo_image: [],
    package_description: '',
    pre_post_images: [],
    package_inclusions: [],
    package_exclusions: [],
    tags: [],
    concerns: [],
  };

  const methods = useForm({
    resolver: yupResolver(packageSchema),
    defaultValues,
  });

  const { control, watch, setValue, handleSubmit, reset } = methods;

  const packagePromoImage = watch('package_promo_image');
  const prePostImages = watch('pre_post_images');

  const fileName = `package_promo_${new Date().toISOString()}`;
  const folderName = `images/packages/${user.clinic_id}`;
  const prePostImagesFileName = `package_pre_post_images_${new Date().toISOString()}`;
  const prePostImagesFolderName = `images/packages/pre_post_images/${user.clinic_id}`;

  const handleProcedureInput = async (e, value, reason) => {
    if (reason !== 'input' || value.length < 2) return;
    setLoadingProcedures(true);
    const results = await searchProcedures(value);
    setProcedureOptions(results || []);
    setLoadingProcedures(false);
  };

  useEffect(() => {
    const allConcerns = selectedProcedures.flatMap((p) => p.concerns || []);
    const uniqueConcerns = Array.from(
      new Map(allConcerns.map((c) => [c.id, c])).values()
    );

    setSelectedConcerns(uniqueConcerns);
    setValue(
      'concerns',
      uniqueConcerns.map((c) => c.id)
    );

    const firstConcern = uniqueConcerns[0];
    setValue('tags', firstConcern?.keywords?.slice(0, 2) || []);
  }, [selectedProcedures, setValue]);

  const onSubmit = (data) => {
    const finalData = {
      clinic_id: user.clinic_id,
      ...data,
      procedure_id: selectedProcedures.map((p) => p.id),
      concerns: selectedConcerns.map((c) => c.id),
    };

    addPackage(finalData)
      .then(() => {
        reset();
        setSelectedProcedures([]);
        setSelectedConcerns([]);
        setSnackbarMessage('Package created successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        navigate(-1);
      })
      .catch((err) => {
        setSnackbarMessage(`Error: ${err.message}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };

  return (
    <Container sx={{ background: { lg: 'white' }, margin: 4, padding: 3, maxWidth: 840, borderRadius: 4 }}>
      <Stack direction="row" gap="14px" alignItems="center" sx={{ marginBottom: '14px' }}>
        <IconButton size="large" onClick={() => navigate(-1)}>
          <Iconify icon="tabler:chevron-left" />
        </IconButton>
        <Typography variant="h4">Create Your Package</Typography>
      </Stack>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={{ xs: '14px', lg: '44px' }}>
            <Grid item xs={12} lg={6}>
              <RHFTextField name="package_name" label="Package Name" required margin="dense" />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Controller
                name="package_doctors"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={availableDoctors || []}
                    getOptionLabel={(o) =>
                      `${o.user_id.first_name} ${o.user_id.last_name}`
                    }
                    onChange={(e, value) =>
                      field.onChange(value.map((v) => v.user_id.id))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Doctors" margin="dense" />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <RHFMarkdownEditor
                name="package_description"
                fileName={`package_image_${new Date().toISOString()}`}
                folderName="images/packages"
                rows={4}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFTextField name="price" label="Package Price" type="number" required margin="dense" />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Autocomplete
                multiple
                options={procedureOptions || []}
                value={selectedProcedures}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                getOptionLabel={(option) =>
                  [option.body_part, option.name].filter(Boolean).join(' → ')
                }
                onChange={(e, v) => setSelectedProcedures(v)}
                onInputChange={handleProcedureInput}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Procedures"
                    margin="dense"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingProcedures && <CircularProgress size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFImageUpload
                initialImage={packagePromoImage}
                fileName={fileName}
                setValue={setValue}
                name="package_promo_image"
                folderName={folderName}
                displayImages
                multipleFiles
                maxFiles={1}
                label="Package Promo Image"
                width={1530}
                height={750}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFImageUpload
                initialImage={prePostImages}
                fileName={prePostImagesFileName}
                setValue={setValue}
                name="pre_post_images"
                folderName={prePostImagesFolderName}
                displayImages
                multipleFiles
                maxFiles={10}
                label="Pre/Post Images"
                width={172}
                height={240}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="concerns"
                control={control}
                render={() => (
                  <Autocomplete
                    multiple
                    freeSolo
                    options={selectedConcerns}
                    value={selectedConcerns}
                    getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
                    onChange={(e, value) => setSelectedConcerns(value)}
                    renderTags={(value, getTagProps) =>
                      value.map((o, i) => (
                        <Chip
                          label={typeof o === 'string' ? o : o.name}
                          {...getTagProps({ index: i })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Concerns" margin="dense" />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value || []}
                    onChange={(e, value) => field.onChange(value)}
                    renderTags={(value, getTagProps) =>
                      value.map((o, i) => (
                        <Chip label={o} {...getTagProps({ index: i })} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Tags" margin="dense" />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  boxShadow: 10,
                  fontSize: { lg: '16px', xs: '12px' },
                  background: '#D92D7F',
                  ':hover': { background: '#D92D7F' },
                }}
              >
                Save Package
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}
